<script setup lang="ts">
import { transform } from 'ol/proj'
import Map from 'ol/Map'
import View from 'ol/View'
import Feature from 'ol/Feature'
import Geometry from 'ol/geom/Geometry'
import { Extent } from 'ol/extent'
import WebGLPointsLayer from 'ol/layer/WebGLPoints'
import VectorSource from 'ol/source/Vector'
import MapBrowserEvent from 'ol/MapBrowserEvent'
import { Coordinate } from 'ol/coordinate'
import Point from 'ol/geom/Point'
import proj4 from 'proj4'
import { register } from 'ol/proj/proj4'
import { useTimeoutFn } from '@vueuse/core'
import { Style, Stroke, Fill } from 'ol/style'
import { altKeyOnly, click, pointerMove } from 'ol/events/condition.js'
import Select from 'ol/interaction/Select.js'
import { StyleFunction } from 'ol/style/Style'
import AppChips from '../app/AppChips.vue'
import MapLayerMunicipalities from '@/components/map/MapLayerMunicipalities.vue'
import BaseLayerTiles from '@/components/map/base-layers/BaseLayerTiles.vue'
import MapControlBox from '@/components/map/controls/MapControlBox.vue'
import { useStationDataStore } from '~/stores/stations'
import { useMapStore } from '~/stores/map'
import { RegionType } from '~/types/types'
import { LayerIdentifier, LayerLegend, LayerPresets } from '~/types/map'
import MapLegends from '@/components/map/MapLegends.vue'
import type { MatchmakingItem } from '~/types/types'

const defaultLayerPresets: LayerPresets = {
  municipalities: { visible: false },
  stations: { visible: true },
  counties: { visible: true },
}
const router = useRouter()
const stationStore = useStationDataStore()
const mapStore = useMapStore()
const { updateUrlQueryParams } = useGlobalFilters()

const props = defineProps<{
  focusToRegion?: { regionType: RegionType; regionId: string }
  focusToStation?: MatchmakingItem | null
  layerPresets?: LayerPresets
  hideTileLayer?: boolean
}>()

onBeforeMount(() => {
  const currentQuery = { ...router.currentRoute.value.query }
  if (currentQuery.focusExtent) {
    allowFocusOperation.value = false
    const values = currentQuery.focusExtent as string
    const toArray = values.split(',').map(Number)
    mapStore.updateFocusExtent(toArray)
  }
})

const focusToStation = computed(() => props.focusToStation)
const layerPresets = props.layerPresets || defaultLayerPresets
const activeDatafilters = computed(() => {
  if (layerPresets) {
    const presets: string[] = []
    Object.values(layerPresets).forEach((preset) => {
      if (preset.dataFilters) {
        presets.push(...preset.dataFilters)
      }
    })
    return presets
  } else {
    return []
  }
})

provide('activeDatafilters', activeDatafilters.value)

// eslint-disable-next-line import/no-named-as-default-member
proj4.defs(
  'EPSG:3006',
  '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs'
)
register(proj4)

function transformCoordinates(coordinates: number[]) {
  return transform(
    coordinates.map((c) => c),
    'EPSG:4326',
    'EPSG:3857'
  )
}
const center = ref(transformCoordinates([11, 58]))
const projection = ref('EPSG:3857')
const zoom = ref(10)
const rotation = ref(0)
const view = ref<View | null>(null)
const map = ref<{
  map: Map
  forEachFeatureAtPixel: (
    pixel: any,
    feature: (feature: Feature<Point>) => void,
    options: any
  ) => void
} | null>(null)

const { exportToPng } = useMapExport(map)
const focusExtent = computed(() => mapStore.focusExtent)
const allowFocusOperation = ref(!focusExtent.value)
const { extent } = useRegionExtents(
  props?.focusToRegion?.regionType,
  props?.focusToRegion?.regionId
)

watch(
  [extent, map],
  ([newValue]) => {
    if (!newValue || !map.value || !allowFocusOperation.value) return
    map.value.map.getView().fit(newValue, {
      size: map.value.map.getSize(),
      duration: 3000,
      padding: [50, 50, 50, 50],
    })
  },
  { immediate: true }
)

watch(map, (newValue, oldValue) => {
  if (oldValue || !view.value) return
  const swedenExtents = [...transformCoordinates([8, 54]), ...transformCoordinates([26, 70])]

  let zoomtoExtent = swedenExtents
  if (focusExtent.value) {
    zoomtoExtent = [...focusExtent.value]
    mapStore.focusExtent = null
  }

  newValue && view.value.fit(zoomtoExtent, { size: newValue.map.getSize() })
  addSelectInteraction()
})

if (focusToStation.value && allowFocusOperation.value) {
  watch(map, (newValue, oldValue) => {
    if (oldValue || !view.value) return
    if (focusToStation.value && focusToStation.value?.locations.length > 1) {
      const calculatedExtent = calculateExtent(focusToStation.value?.locations) as Extent
      newValue?.map.getView().fit(calculatedExtent, {
        size: newValue?.map.getSize(),
        duration: 3000,
        padding: [50, 50, 50, 50],
      })
    } else {
      const coordinates = [
        focusToStation.value?.locations[0].lng,
        focusToStation.value?.locations[0].lat,
      ] as Array<number>
      newValue?.map.getView().animate({
        zoom: 12,
        center: transformCoordinates(coordinates),
        duration: 1200,
      })
    }
  })
}
function calculateExtent(coordinates: any) {
  if (coordinates.length === 0) return null

  let minLat = Number.POSITIVE_INFINITY
  let maxLat = Number.NEGATIVE_INFINITY
  let minLng = Number.POSITIVE_INFINITY
  let maxLng = Number.NEGATIVE_INFINITY

  for (const coord of coordinates) {
    const lat = coord.lat
    const lng = coord.lng
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  }

  const minCords = transformCoordinates([minLng, minLat])
  const maxCords = transformCoordinates([maxLng, maxLat])

  return [minCords[0], minCords[1], maxCords[0], maxCords[1]]
}

type VectorSelect = {
  info: string | null
  position: Coordinate | null
  link: string | null
  show: boolean
  positioning: 'center-center' | 'top-center'
  dataValue?: number | null
  feature: Feature<Geometry> | null
  overlayType: 'mouse' | 'feature'
  suffix: string | null
}

const vectorSelect = ref<VectorSelect>({
  info: null,
  position: null,
  positioning: 'center-center',
  link: null,
  show: false,
  dataValue: null,
  feature: null,
  overlayType: 'feature',
  suffix: null,
})

const selectStyle = new Style({
  fill: new Fill({ color: 'inherit' }),
  stroke: new Stroke({
    color: '#000',
    width: 2,
  }),
  zIndex: 1,
})

const overlayTypePositioning: { [key: string]: 'bottom-center' | 'center-center' } = {
  mouse: 'bottom-center',
  feature: 'center-center',
}

function setSelectedStyle(feature: Feature<Geometry>) {
  const color = feature.get('color')
  selectStyle.setFill(new Fill({ color: color || 'transparent' }))
  return selectStyle
}
function addSelectInteraction() {
  const selectPointerMove = new Select({
    condition: pointerMove,
    filter: function (feature, layer) {
      return layer.className_ === 'vectorlayer'
    },
    style: setSelectedStyle as StyleFunction,
  })

  map.value && map.value.map.addInteraction(selectPointerMove)

  selectPointerMove.on('select', function (e) {
    // console.log('SELECT', e?.selected[0].get('county_code'))
    if (e.deselected.length > 0) {
      vectorSelect.value.show = false
    }
    if (e.selected.length > 0) {
      const feature = e.selected[0]
      const overLayType = feature.get('overlayType')
      vectorSelect.value.feature = feature
      vectorSelect.value.info = feature.get('label')
      vectorSelect.value.dataValue = feature.get('dataValue')
      vectorSelect.value.suffix = feature.get('suffix')
      vectorSelect.value.link = feature.get('link') || null
      vectorSelect.value.overlayType = overLayType
      vectorSelect.value.positioning =
        overlayTypePositioning[overLayType] || overlayTypePositioning.feature
      if (feature.get('center_east')) {
        vectorSelect.value.position = [feature.get('center_east'), feature.get('center_north')]
      }

      if (overLayType === 'mouse') {
        vectorSelect.value.position = e.mapBrowserEvent.coordinate
      }

      if (vectorSelect.value.info) {
        vectorSelect.value.show = true
      }
    }
  })
}

function showLayer(layerIdentifier: LayerIdentifier) {
  const layerIsPresentInPresets = layerPresets[layerIdentifier]
  return layerIsPresentInPresets
}

function getLayerVisibility(layerIdentifier: LayerIdentifier) {
  let visibility = layerPresets[layerIdentifier]?.visible
  if (Object.keys(mapStore.layerVisibilityOveride).length !== 0) {
    if (mapStore.layerVisibilityOveride[layerIdentifier] !== undefined) {
      visibility = mapStore.layerVisibilityOveride[layerIdentifier]
      delete mapStore.layerVisibilityOveride[layerIdentifier]
    }
  }
  return visibility || false
}

function getLayerName(layerIdentifier: LayerIdentifier, defaultName: string) {
  const name = layerPresets[layerIdentifier]?.name
  return name || defaultName
}
function getLayerDataLegend(layerIdentifier: LayerIdentifier) {
  const legend = layerPresets[layerIdentifier]?.dataLegendLabel
  return legend || null
}

function mapMoveEvent() {
  const extent = view.value?.calculateExtent(map.value?.map.getSize())
  mapStore.activeExtent = extent
}

const mapClick = (event: MapBrowserEvent<UIEvent>) => {
  // return

  if (event?.pixel) {
    if (map?.value) {
      map.value.forEachFeatureAtPixel(
        event.pixel,
        function (feature: Feature<Point>) {
          // if (selected.value) {
          //   selected.value.set('selected', '0')
          // }
          // selected.value = feature
          // feature.set('selected', '1')
          if (feature.get('origin') === 'uppladdning') {
            window.open(
              `https://uppladdning.nu/SiteDetail.aspx?id=${feature.get('origin_id')}`,
              '_blank'
            )
          }
          if (feature.get('slug')) {
            router.push({
              path: `/samarbeta/${feature.get('slug')}`,
            })
          }
        },
        {
          layerFilter: function (layer: WebGLPointsLayer<VectorSource<Point>>) {
            return layer.className_ === 'webglpointlayer'
          },
        }
      )
    }
  }
}

const { isPending, start, stop } = useTimeoutFn(() => {
  hovered.value?.set('hover', '0')
  hovered.value = null
}, 100)

const mouseCoordinates = ref([0, 0])

// hover functionality
const mapPointerMove = (event: MapBrowserEvent<UIEvent>) => {
  if (event?.pixel && map?.value) {
    if (vectorSelect.value.show && vectorSelect.value.feature?.get('overlayType') === 'mouse') {
      vectorSelect.value.position = event.coordinate
    }

    mouseCoordinates.value = event.coordinate
    let hits = 0
    map.value.forEachFeatureAtPixel(
      event.pixel,
      (feature) => {
        hits++
        if (feature.get('id') !== hoveredId.value) {
          hovered.value?.set('hover', '0')
        }
        overlayPosition.value = feature!.getGeometry()!.getCoordinates()
        overlayText.value = feature?.get('name')

        const fuelTypeIds = feature.get('fuel_type_ids') as number[] | undefined
        if (fuelTypeIds)
          overlayFuelType.value = fuelTypeIds.map((id) => stationStore.getFuelType(id)!.title)

        if (isPending) {
          stop()
        }

        feature.set('hover', '1')
        hovered.value = feature
        hoveredId.value = feature.get('id')
      },
      {
        layerFilter: function (layer: WebGLPointsLayer<VectorSource<Point>>) {
          return layer.className_ === 'webglpointlayer'
        },
      }
    )
    if (hits === 0) {
      if (hovered.value) {
        start()
      }
    }

    // if (selectedVector.value !== null) {
    //   console.log('nolla selectedVector')
    //   //selectedVector.value.setStyle(undefined)
    //   //selectedVector.value = null
    // }
    // map.value.forEachFeatureAtPixel(
    //   event.pixel,
    //   function (feature) {
    //     if (feature.get('county_code') !== selectedVector.value?.get('county_code')) {
    //       console.log(feature.get('county_name'))
    //       feature.setStyle(selectStyle)
    //     }
    //     selectedVector.value = feature
    //   },
    //   {
    //     layerFilter: function (layer) {
    //       return layer.className_ === 'vectorlayer'
    //     },
    //   }
    // )
  }
}

const currentZoom = ref(0)
function zoomChanged(z) {
  currentZoom.value = z
}

const overlayPosition = ref<Coordinate>([40, 40])
const overlayText = ref('empty')
const overlayFuelType = ref<string[]>([])
const selected = ref<Feature | null>(null)
const hovered = ref<Feature | null>(null)
const hoveredId = ref<number | null>(null)

const selectedVector = ref<Feature | null>(null)

const sidebarVisible = computed({
  // getter
  get() {
    return !!selected.value
  },
  // setter
  set(newValue) {
    if (newValue === false) {
      if (selected.value) {
        selected.value.set('hover', '0')
        selected.value.set('selected', '0')
        selected.value = null
      }

      if (hovered.value) {
        hovered.value.set('hover', '0')
        hovered.value.set('selected', '0')
        hovered.value = null
        hoveredId.value = null
      }
    }
  },
})
function parseVectorHoverValue(value: number | undefined) {
  return (isNaN(value) && value) || value?.toFixed(2)
}
/** Legend */
const layerLegends = ref<LayerLegend[]>([])
function registerLegend(legend: LayerLegend) {
  const existingLegend = layerLegends.value.find((l) => l.identifier === legend.identifier)
  if (existingLegend) {
    existingLegend.name = legend.name
    existingLegend.gradientData = legend.gradientData
    existingLegend.spriteData = legend.spriteData
  } else {
    layerLegends.value.push(legend)
  }
}
const hasVisibleLegends = computed(() => {
  return layerLegends.value.some((l) => l.visible)
})

provide('layerLegends', { registerLegend, legends: layerLegends })
</script>

<template>
  <ClientOnly>
    <ol-map
      ref="map"
      :load-tiles-while-animating="true"
      :load-tiles-while-interacting="true"
      :pixel-ratio="2"
      class="map"
      @click="mapClick"
      @pointermove="mapPointerMove"
      @moveend="mapMoveEvent"
    >
      <ol-view
        ref="view"
        :center="center"
        :rotation="rotation"
        :zoom="zoom"
        :projection="projection"
        :max-zoom="17"
        @zoom-changed="zoomChanged"
      />
      <ol-attribution-control />
      <ol-scaleline-control />
      <ol-zoom-control />

      <ol-overlay
        v-if="hovered"
        ref="stationsOverlay"
        :position="overlayPosition"
        positioning="bottom-center"
        :offset="[0, -currentZoom * 1.3]"
      >
        <template #default>
          <div class="overlay-map mouse" :class="{ link: hovered.get('origin') === 'uppladdning' }">
            <div class="marker-type">
              <span v-if="hovered.get('pointType') === 'matchmaking'">Inlägg från samarbeta </span>
              <span v-if="hovered.get('pointType') === 'station'">
                Drivmedelsstation
                <span v-if="hovered.get('type') === 'existing'"> (befintlig)</span>
                <span v-if="hovered.get('type') === 'planned'"> (planerad)</span>
              </span>
            </div>
            <div class="marker-title">
              {{ overlayText }}
            </div>
            <div v-if="overlayFuelType.length" class="marker-label">Drivmedel</div>
            <AppChips v-if="overlayFuelType.length" :items="overlayFuelType" />
            <div class="marker-cta">
              <span v-if="hovered.get('origin') === 'uppladdning'">
                Klicka för att gå till stationen på uppladdning.nu</span
              >
              <span v-if="hovered.get('pointType') === 'matchmaking' && !focusToStation">
                Klicka för att visa Inlägget</span
              >
            </div>
          </div>
        </template>
      </ol-overlay>

      <ol-overlay
        v-if="vectorSelect.show && currentZoom > 4"
        ref="vectorOverlay"
        :position="vectorSelect.position"
        :positioning="vectorSelect.positioning"
        :stop-event="false"
      >
        <template #default>
          <div
            class="overlay-map vectorlayer"
            :class="{ mouse: vectorSelect.overlayType === 'mouse' }"
          >
            <NuxtLink
              v-if="vectorSelect.link"
              class="overlay-link"
              :to="vectorSelect.link"
              :target="
                vectorSelect.feature?.get('type') === 'electricityGridOwner' ? '_blank' : null
              "
            >
              {{ vectorSelect.info }}
            </NuxtLink>

            <span v-else> {{ vectorSelect.info }}</span>
            <div v-if="vectorSelect.dataValue !== undefined" class="data-value">
              {{ parseVectorHoverValue(vectorSelect.dataValue) }}{{ vectorSelect.suffix }}
            </div>
          </div>
        </template>
      </ol-overlay>

      <div class="map-controls">
        <MapSettings>
          <MapLayerControl
            v-if="showLayer('electricity-grid')"
            name="Elnätsägare"
            identifier="electricity-grid"
            :data-legend-label="getLayerDataLegend('stations')"
            :visible="getLayerVisibility('electricity-grid')"
          >
            <MapLayerElectricityGridOwners />
          </MapLayerControl>

          <MapLayerControl
            v-if="showLayer('municipalities')"
            name="Kommuner"
            identifier="municipalities"
            :visible="getLayerVisibility('municipalities')"
          >
            <MapLayerMunicipalities />
          </MapLayerControl>

          <MapLayerControl
            v-if="showLayer('area-heat-map')"
            :name="getLayerName('area-heat-map', 'Statistiklager')"
            :data-legend-label="getLayerDataLegend('area-heat-map')"
            identifier="area-heat-map"
            :visible="getLayerVisibility('area-heat-map')"
          >
            <MapLayerAreaHeatMap />
          </MapLayerControl>

          <MapLayerControl
            v-if="showLayer('counties')"
            name="Län"
            identifier="counties"
            :visible="getLayerVisibility('counties')"
          >
            <MapLayerCounties />
          </MapLayerControl>

          <MapLayerControl
            v-if="showLayer('stations')"
            :name="getLayerName('stations', 'Drivmedelsstationer')"
            :data-legend-label="getLayerDataLegend('stations')"
            identifier="stations"
            :visible="getLayerVisibility('stations')"
          >
            <MapLayerStations />
          </MapLayerControl>
          <MapLayerControl
            v-if="showLayer('infrastructure-opportunities')"
            :name="getLayerName('infrastructure-opportunities', 'Etableringsbehov')"
            identifier="infrastructure-opportunities"
            :visible="getLayerVisibility('infrastructure-opportunities')"
          >
            <MapLayerMatchmaking />
          </MapLayerControl>

          <BaseLayerTiles v-if="!hideTileLayer" tile-source="lantmateriet" />
        </MapSettings>
        <MapControlBox icon="pi-download" label="Exportera kartan" :expanded="false">
          <p>Spara den aktuella kartan som en bild här. Kartbilden exporteras i PNG-format.</p>

          <Button label="Ladda ner" rounded @click="exportToPng()" />
        </MapControlBox>
        <MapControlBox icon="pi-share-alt" label="Dela" :expanded="false">
          <MapSharingBox />
        </MapControlBox>
      </div>
      <div v-if="hasVisibleLegends" class="map-legend">
        <MapControlBox
          v-if="layerLegends.length > 0"
          icon="pi-info-circle"
          label="Teckenförklaring"
          :expanded="focusToStation ? false : true"
        >
          <MapLegends type="ui" />
        </MapControlBox>
      </div>
    </ol-map>
    <Sidebar v-model:visible="sidebarVisible" :modal="false" position="bottom" :dismissable="false">
      <h2>{{ selected?.get('name') }}</h2>
    </Sidebar>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.map {
  position: relative;
  height: 100%;
  width: 100%;
}

.map-controls {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.map-legend {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  z-index: 1;
}
.overlay-map {
  background-color: white;
  // border: 2px solid #ccc;
  box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.25);
  // padding: 4px 10px;
  // border-radius: 2px;
  border-radius: 20px;
  padding: 8px 16px;

  -webkit-user-select: none;
  /* Safari */
  -ms-user-select: none;
  /* IE 10 and IE 11 */
  user-select: none;
  &.mouse {
    position: relative;
    bottom: 12px;

    &:before {
      content: ' ';
      position: absolute;
      width: 0;
      height: 0;
      left: calc(50% - 1px);
      bottom: -12px;
      border: 8px solid;
      border-color: #fff transparent transparent #fff;
    }
  }
  .marker-type,
  .marker-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #777;
    margin-bottom: 0.25rem;
  }
  .marker-title {
    font-size: 1.2rem;
    color: #000;
    margin-bottom: 0.5rem;
  }
  .marker-cta {
    margin-top: 0.75rem;
    font-size: 0.75rem;
    color: #777;
  }
  .data-value {
    font-size: 1rem;
    font-weight: bold;
    text-align: center;
  }
  /* Standard syntax */
  &.station {
    pointer-events: none;
  }
}
</style>
